import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { performanceEfficiency, type EfficiencyBoard, type EfficiencyLine, type EfficiencyTag } from '@aithora/shared';
import { sx } from '../../ui/designStyles';
import { usePerf } from './ctx';
import { marginPct, notServed, pre, usePerfApi } from './lib';
import { BuildTagged, useCustomerText } from '../../ui/buildLabels';
import { useConsoleRole } from '../../session/SessionContext';
import { sectionsForRole } from '../settings/lens';

const GREEN = '#1F6B52';
const RED = '#B3261E';
const GREY = '#54606C';
const INK = '#22272E';
const SLATE = '#3C454F';
const FLOW_C = [GREEN, '#2F5C97', '#7FA8D4', 'rgba(34,39,46,.28)'];
const FILL_C = [RED, SLATE, GREEN];
const SHARE_C = [GREEN, '#2F5C97', '#7FA8D4', SLATE, 'rgba(34,39,46,.28)'];

/** Dollars as the design writes them (k$, L43272). */
const k$ = (n: number) => (Math.abs(n) >= 1000 ? '$' + (n / 1000).toFixed(n >= 10000 ? 0 : 1) + 'K' : '$' + Math.round(n));
const money = (n: number) => '$' + Math.round(n).toLocaleString('en-US');
const usd = (n: number) => (n < 0 ? '−' : '') + '$' + Math.abs(Math.round(n));
const grp = (n: number) => Math.round(n).toLocaleString('en-US');

const REG_WRAP = 'box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:6px 11px 7px;min-width:0;break-inside:avoid;margin-bottom:7px;';
const REG_HEAD = 'font-size:16px;font-weight:800;letter-spacing:.05em;text-transform:uppercase;color:' + INK + ';min-width:0;white-space:normal;overflow-wrap:break-word;line-height:1.2;';
const RUN_TAG = 'font-size:14px;font-weight:700;letter-spacing:.03em;padding:0 4px;border-radius:3px;white-space:nowrap;color:' + SLATE + ';background:transparent;border:1px dashed rgba(34,39,46,.30);';
const SUM = (c: string) => 'font-size:14px;font-weight:800;font-variant-numeric:tabular-nums;white-space:nowrap;color:' + c + ';';
const NOTE = 'font-size:14px;color:#54606C;line-height:1.3;margin-top:4px;text-wrap:pretty;';

/** The basis dot on a line (design dot(), L43467). */
const dot = (b: string) =>
  'display:inline-block;width:6px;height:6px;border-radius:2px;flex:0 0 6px;background:' + (b === 'OURS' ? GREEN : b === 'CMS' ? '#2F5C97' : b === 'MODEL' ? SLATE : 'rgba(34,39,46,.22)') + ';';

/** A strip tag (design tagStyle): OURS green, INDEX blue, anything modeled dashed. */
const tagStyle = (t: string) =>
  t
    ? 'font-size:14px;font-weight:700;letter-spacing:.03em;padding:0 4px;border-radius:3px;' +
      (t === 'OURS' ? 'color:' + GREEN + ';background:rgba(47,138,111,.11);' : t === 'INDEX' ? 'color:#2F5C97;background:rgba(79,138,214,.12);' : 'color:' + SLATE + ';background:transparent;border:1px dashed rgba(34,39,46,.30);')
    : 'display:none;';

/** One tag for several lines: theirs when they agree, PART MODEL when some of it is modeled. */
function mixTag(lines: EfficiencyLine[]): EfficiencyTag {
  const set = [...new Set(lines.map((l) => l.basis))];
  if (set.length === 1) return set[0];
  return 'PART MODEL';
}

/** A line's value as the panels print it (design coStacks v, L43553). */
function lineValue(r: EfficiencyLine): string {
  if (r.savedUsd > 0) return '+' + k$(r.savedUsd);
  if (r.atRiskUsd > 0) return '−' + k$(r.atRiskUsd);
  if (r.now !== null && r.now !== undefined) return r.unit === '$' ? k$(r.now) : Number(r.now).toFixed(r.now % 1 ? 1 : 0) + (r.unit ? ' ' + r.unit : '');
  if (r.before !== null) return r.unit === '$' ? k$(r.before) : r.before + (r.unit ? ' ' + r.unit : '');
  return '·';
}

/**
 * Efficiency (design isCareOps, T16130-16402): the operating register. What Aithora
 * returned (hours, dollars), what is at risk, cost per resident day, and the lines that
 * wait on a feed. Occupancy and the operating margin come from the building's benchmark;
 * the register itself is PROPOSED (GET /performance/efficiency) and says so when absent.
 */
export function EfficiencyTab() {
  const plain = useCustomerText();
  const { fac, op } = usePerf();
  const facility = op ? fac?.orgId ?? undefined : undefined;
  const q = usePerfApi((c, signal) => performanceEfficiency(c, { query: { facility }, signal }), [facility], op ? !!facility : true);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [tall, setTall] = useState(() => (typeof window !== 'undefined' ? window.innerHeight >= 1050 : false));
  const [lockPref, setLockPref] = useState<boolean | null>(null);
  // V9 summary first: each panel shows its headline; a click opens the detail in place.
  const [openP, setOpenP] = useState<Record<string, boolean>>({});
  const { role } = useConsoleRole();
  const navigate = useNavigate();
  const canConnect = !!role && sectionsForRole(role).includes('Integrations');
  const isOpen = (k: string) => !!openP[k];
  const head = (k: string, children: ReactNode, gap = 6) => (
    <div
      role="button"
      tabIndex={0}
      aria-expanded={isOpen(k)}
      data-testid={'eff-head-' + k}
      onClick={() => setOpenP((o) => ({ ...o, [k]: !o[k] }))}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setOpenP((o) => ({ ...o, [k]: !o[k] }));
        }
      }}
      style={sx('cursor:pointer;display:flex;align-items:baseline;gap:' + gap + 'px;flex-wrap:wrap;row-gap:2px;min-width:0;padding-bottom:' + (gap === 7 ? 3 : 4) + 'px;')}
    >
      {children}
      <span aria-hidden="true" style={sx('margin-left:4px;font-size:14px;color:#3C454F;')}>{isOpen(k) ? '▾' : '▸'}</span>
    </div>
  );
  useEffect(() => {
    const on = () => setTall(window.innerHeight >= 1050);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);

  const b: EfficiencyBoard | null = q.data;
  const residents = fac?.residents ?? null;
  const beds = fac?.beds ?? null;
  const occReal = residents != null && beds ? (residents / beds) * 100 : null;
  const margin = marginPct(fac?.metrics.operatingMargin?.value);

  const L = b?.lines ?? [];
  const byKey = new Map(L.map((r) => [r.key, r]));
  const savedTotal = L.reduce((t, r) => t + r.savedUsd, 0);
  const riskTotal = L.reduce((t, r) => t + r.atRiskUsd, 0);
  const liveN = L.filter((r) => r.basis !== 'LOCKED').length;
  const lockedN = L.length - liveN;
  const maxMag = Math.max(1, ...L.map((r) => Math.max(r.savedUsd, r.atRiskUsd)));
  // Unmeasured figures arrive as null (basis LOCKED) and print as an en dash, never 0.
  const hrsTotal = b?.hoursReturned.value ?? null;
  const dashOr = (n: number | null | undefined, f: (x: number) => string) => (n == null ? '–' : f(n));
  const leak = byKey.get('leak');
  const openRow = openKey ? byKey.get(openKey) ?? null : null;
  const lockOpen = lockPref === null ? tall : lockPref;
  const bStyle = (v: number, tone: string) =>
    'display:block;height:7px;border-radius:2px;background:' + tone + ';width:' + Math.max(2, Math.round((v / maxMag) * 100)) + '%;animation:growW .5s cubic-bezier(.22,.61,.36,1);transform-origin:left;';

  const strip: [string, string, string, string][] = [
    ['Hrs returned', b ? dashOr(hrsTotal, grp) : '–', GREEN, b?.hoursReturned.basis ?? ''],
    ['Saved / mo', b ? k$(savedTotal) : '–', GREEN, b ? mixTag(L.filter((r) => r.savedUsd > 0)) : ''],
    ['At risk / mo', b ? k$(riskTotal) : '–', RED, b ? mixTag(L.filter((r) => r.atRiskUsd > 0)) : ''],
    ['Occupancy', occReal != null ? occReal.toFixed(1) + '%' : '–', occReal != null && occReal >= 86.7 ? GREEN : RED, byKey.get('occ')?.basis ?? ''],
    ['Margin', margin != null ? margin.toFixed(1) + '%' : '–', margin != null && margin > -5.9 ? GREEN : RED, b?.marginBasis ?? 'CMS'],
    ['Codes open', leak ? k$(leak.atRiskUsd) : '–', RED, leak?.basis ?? ''],
    ['Labor % · US', b ? dashOr(b.indices.labor.value, (v) => v.toFixed(1) + '%') : '–', GREY, b?.indices.labor.basis ?? ''],
    ['CNA turn · US', b ? dashOr(b.indices.cnaTurnover.value, (v) => v.toFixed(1) + '%') : '–', GREY, b?.indices.cnaTurnover.basis ?? ''],
    ['Lines live', b ? liveN + ' of ' + L.length : '–', GREY, ''],
  ];

  const open = (key: string) => (e: React.MouseEvent) => {
    pre(e);
    setOpenKey((k) => (k === key ? null : key));
  };

  // Payer day rates against the cost of a day, pinned to the measured margin (design coPpd).
  const payers = b?.ppd.payers ?? [];
  const blendedPPD = payers.reduce((t, p) => t + p.ratePerDay * p.share, 0);
  const costPPD = margin != null ? blendedPPD * (1 - margin / 100) : null;
  const maxPPD = Math.max(638, ...payers.map((p) => p.ratePerDay), costPPD ?? 0) * 1.04;
  const medicaid = payers.find((p) => p.name === 'Medicaid');
  const revMo = residents != null ? residents * blendedPPD * 30.4 : 0;

  const Q = b?.quarter;
  const WK = Q?.weeksHours.length ?? 13;
  const maxWk = Math.max(1, ...(Q?.weeksHours ?? []));
  const maxCap = Math.max(1, ...(Q?.weeksCapturedUsd ?? []));

  const doNow = L.filter((r) => r.basis !== 'LOCKED' && Math.max(r.savedUsd, r.atRiskUsd) > 0).sort((a, c) => Math.max(c.savedUsd, c.atRiskUsd) - Math.max(a.savedUsd, a.atRiskUsd));
  const sel = (key: string) => (key === openKey ? 'background:rgba(79,138,214,.09);' : '');

  const waterfall: [string, number, string][] = b
    ? [
        ['Paperwork saved', (byKey.get('doc')?.savedUsd ?? 0) + (byKey.get('mds')?.savedUsd ?? 0), GREEN],
        ['Capture booked', byKey.get('cap')?.savedUsd ?? 0, GREEN],
        ['Empty beds', -(byKey.get('beds')?.atRiskUsd ?? 0), RED],
        ['Uncaptured codes', -(leak?.atRiskUsd ?? 0), RED],
        ['PDPM uplift', -(byKey.get('pdpm')?.atRiskUsd ?? 0), RED],
        ['Readmission penalty', -(byKey.get('readm')?.atRiskUsd ?? 0), RED],
      ]
    : [];

  const busy = q.loading && !b;
  const missing = notServed(q.error);

  return (
    <>
    <div data-testid="eff-hours" style={sx('flex:0 0 auto;display:flex;align-items:center;gap:8px 14px;flex-wrap:wrap;padding:10px 14px;margin-bottom:8px;border-radius:10px;border:1px solid rgba(34,39,46,.12);background:#FCFAF4;')}>
      <div style={sx('display:flex;flex-direction:column;gap:2px;flex:1 1 320px;min-width:0;')}>
        <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;')}>
          <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:700;')}>Staff hours returned</span>
          {b && (
            <BuildTagged tag={b.hoursReturned.basis}>
              <span style={sx('font-size:14px;font-weight:800;letter-spacing:.07em;color:#264C7E;background:rgba(47,92,151,.08);border-radius:3px;padding:2px 5px;')}>{b.hoursReturned.basis}</span>
            </BuildTagged>
          )}
        </div>
        <div style={sx('font-size:20px;font-weight:800;color:' + (hrsTotal == null ? GREY : INK) + ';font-variant-numeric:tabular-nums;')}>
          {!b ? '–' : hrsTotal == null ? 'Not measured yet' : grp(hrsTotal * 12) + ' hours a year'}
        </div>
        <div style={sx('font-size:16px;color:#54606C;line-height:1.45;text-wrap:pretty;')}>
          {(b?.hoursReturned.reason ? b.hoursReturned.reason + ' ' : '') + 'Becomes measured when payroll or timekeeping is connected.'}
        </div>
      </div>
      {canConnect && (
        <a
          href="#"
          onClick={(e) => {
            pre(e);
            navigate('/settings?section=integrations');
          }}
          style={sx('display:inline-flex;align-items:center;min-height:36px;padding:0 14px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#264C7E;border:1px solid rgba(47,92,151,.4);')}
        >
          Connect payroll to measure it
        </a>
      )}
    </div>
    <div data-screen-label="Efficiency" data-testid="performance-efficiency" aria-busy={busy ? 'true' : undefined} style={sx('position:relative;display:flex;flex-direction:column;gap:2px;min-width:0;flex:1 1 auto;min-height:0;height:100%;overflow-x:hidden;overflow-y:auto;')}>
      <div style={sx('flex:0 0 auto;display:flex;flex-wrap:wrap;min-width:0;border-top:1px solid rgba(34,39,46,.14);border-bottom:1px solid rgba(34,39,46,.14);')} data-testid="eff-strip">
        {strip.map(([k, v, c, t], i) => (
          <div key={k} style={sx('box-sizing:border-box;display:flex;align-items:baseline;gap:5px;padding:4px 9px;min-width:0;white-space:nowrap;' + (i ? 'border-left:1px solid rgba(34,39,46,.10);' : ''))}>
            <span style={sx('font-size:14px;letter-spacing:.04em;text-transform:uppercase;color:' + GREY + ';font-weight:700;')}>
              <span>{k}</span>
            </span>
            <span style={sx('font-size:16px;font-weight:800;font-variant-numeric:tabular-nums;letter-spacing:-.01em;color:' + c + ';')}>
              <span>{v}</span>
            </span>
            <BuildTagged tag={t} keepSlot>
              <span style={sx(tagStyle(t))}>
                <span>{t}</span>
              </span>
            </BuildTagged>
          </div>
        ))}
      </div>

      {q.error && !b && (
        <div role={missing ? undefined : 'alert'} data-testid="empty-state" style={sx('font-size:16px;color:' + (missing ? GREY : RED) + ';padding:8px 11px;')}>
          {missing ? plain('Not served yet: the operating register (hours returned, cost per resident day, payroll, agency and overtime) waits on GET /performance/efficiency.') : q.error.message}
        </div>
      )}

      {b && (
        <>
          <div data-testid="eff-grid" style={sx('flex:0 0 auto;min-height:0;min-width:0;display:grid;grid-template-columns:repeat(auto-fill,minmax(300px,1fr));gap:4px 16px;align-items:start;')}>
            <div style={sx('display:contents;')}>
              <div style={sx(REG_WRAP)} data-testid="eff-delta">
                {head(
                  'delta',
                  <>
                    <span style={sx(REG_HEAD)}>Before Aithora {'→'} now</span>
                    <span style={sx('flex:1 1 4px;')} />
                    <span style={sx(SUM(GREEN))}>
                      <span>{dashOr(hrsTotal, (h) => '+' + grp(h)) + ' hrs  ·  +' + k$(savedTotal)}</span>
                    </span>
                  </>,
                )}
                {isOpen('delta') && b.delta.map((d, i) => {
                  const fmt = (n: number) => (d.unit === '$' ? k$(n) : Number(n).toFixed(n % 1 ? 1 : 0));
                  const isNew = d.before === 0;
                  const span = Math.max(d.before, d.now, 1);
                  const better = d.lowerIsBetter ? d.now < d.before : d.now > d.before;
                  const pct = d.before ? Math.round((Math.abs(d.now - d.before) / d.before) * 100) : 100;
                  const cellEllip = 'min-width:0;text-align:right;font-size:16px;font-variant-numeric:tabular-nums;';
                  return (
                    <div key={d.label} style={sx('display:grid;grid-template-columns:minmax(0,2.2fr) minmax(14px,.32fr) max-content 9px max-content max-content;gap:4px;align-items:center;padding:2px 0;min-width:0;animation:riseIn .24s cubic-bezier(.22,.61,.36,1);animation-delay:' + i * 18 + 'ms;')}>
                      <span style={sx('min-width:0;font-size:14px;color:' + INK + ';line-height:1.2;white-space:normal;overflow-wrap:anywhere;')}>
                        <span>{d.label}</span>
                      </span>
                      <span style={sx('position:relative;display:block;height:8px;border-radius:2px;background:rgba(34,39,46,.07);overflow:hidden;')}>
                        <i style={sx('position:absolute;left:0;top:0;bottom:0;border-radius:2px;background:rgba(34,39,46,.18);width:' + Math.max(2, Math.round((d.before / span) * 100)) + '%;')} />
                        <i style={sx('position:absolute;left:0;top:0;bottom:0;border-radius:2px;background:' + (better ? GREEN : RED) + ';width:' + Math.max(2, Math.round((d.now / span) * 100)) + '%;animation:growW .5s cubic-bezier(.22,.61,.36,1);transform-origin:left;')} />
                      </span>
                      <span style={sx(cellEllip + 'color:' + GREY + ';white-space:nowrap;')}>
                        <span>{isNew ? '–' : fmt(d.before)}</span>
                      </span>
                      <span style={sx('font-size:14px;color:' + GREY + ';text-align:center;')}>{'→'}</span>
                      <span style={sx(cellEllip + 'font-weight:800;color:' + INK + ';white-space:nowrap;')}>
                        <span>{fmt(d.now)}</span>
                      </span>
                      <span style={sx(cellEllip + 'font-weight:700;color:' + (better ? GREEN : RED) + ';')}>
                        <span>{isNew ? 'new' : (better ? (d.lowerIsBetter ? '−' : '+') : '') + pct + '%'}</span>
                      </span>
                    </div>
                  );
                })}
                {isOpen('delta') && (
                  <div style={sx(NOTE)}>
                    <span>{b.deltaNote}</span>
                  </div>
                )}
              </div>

              {Q && (
                <div style={sx(REG_WRAP)} data-testid="eff-quarter">
                  {head('quarter', <>
                    <span style={sx(REG_HEAD)}>
                      <span>{Q.label}</span>
                    </span>
                    <BuildTagged tag={Q.basis}><span style={sx(RUN_TAG)}>
                      <span>{Q.basis}</span>
                    </span></BuildTagged>
                    <span style={sx('flex:1 1 4px;')} />
                    <span style={sx(SUM(GREEN))}>
                      <span>{k$(Q.valueUsd) + '  ·  ' + dashOr(Q.totalHours, grp) + ' hrs'}</span>
                    </span>
                  </>, 6)}
                  {isOpen('quarter') && (<>
                  <div style={sx('display:flex;gap:7px;min-width:0;')}>
                    <div style={sx('flex:0 0 auto;display:flex;flex-direction:column;justify-content:space-between;height:74px;font-size:14px;font-variant-numeric:tabular-nums;color:#54606C;text-align:right;white-space:nowrap;')}>
                      <span>
                        <span>{grp(maxWk) + ' hrs'}</span>
                      </span>
                      <span>
                        <span>{grp(maxWk / 2)}</span>
                      </span>
                      <span>0</span>
                    </div>
                    <div style={sx('flex:1 1 auto;min-width:0;position:relative;height:74px;border-left:1px solid rgba(34,39,46,.14);border-bottom:1px solid rgba(34,39,46,.14);')}>
                      <i style={sx('position:absolute;left:0;right:0;top:0;height:1px;background:rgba(34,39,46,.07);')} />
                      <i style={sx('position:absolute;left:0;right:0;top:50%;height:1px;background:rgba(34,39,46,.07);')} />
                      <div style={sx('position:absolute;inset:0;display:flex;align-items:flex-end;gap:3px;padding:0 2px;')}>
                        {Q.weeksHours.map((v, i) => (
                          <span key={i} style={sx('flex:1 1 0;min-width:0;display:flex;flex-direction:column;justify-content:flex-end;height:100%;')}>
                            <i style={sx('display:block;width:100%;border-radius:2px 2px 0 0;height:' + Math.max(5, Math.round((v / maxWk) * 100)) + '%;background:' + (i === WK - 1 ? GREEN : 'rgba(47,138,111,.38)') + ';animation:growH .45s cubic-bezier(.22,.61,.36,1);transform-origin:bottom;animation-delay:' + i * 22 + 'ms;')} />
                            <i style={sx('display:block;width:100%;height:' + Math.max(2, Math.round(((Q.weeksCapturedUsd[i] ?? 0) / maxCap) * 22)) + '%;background:#2F5C97;opacity:.55;')} />
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div style={sx('display:flex;gap:2px;margin-top:2px;min-width:0;')}>
                    <span style={sx('flex:0 0 auto;width:34px;')} />
                    {['W1', 'W' + Math.ceil(WK / 2), 'W' + WK + ' · now'].map((t, i) => (
                      <span key={t} style={sx('flex:1 1 0;min-width:0;font-size:14px;font-variant-numeric:tabular-nums;color:' + (i === 2 ? INK : GREY) + ';font-weight:' + (i === 2 ? '700' : '500') + ';' + (i === 0 ? 'text-align:left;' : i === 1 ? 'text-align:center;' : 'text-align:right;'))}>
                        <span>{t}</span>
                      </span>
                    ))}
                  </div>
                  <div style={sx('display:flex;flex-wrap:wrap;gap:4px 12px;margin-top:4px;min-width:0;')}>
                    {(
                      [
                        ['Hrs returned / wk', GREEN, grp(Q.weeksHours[WK - 1] ?? 0) + ' hrs'],
                        ['Captured / wk', '#2F5C97', k$(Q.weeksCapturedUsd[WK - 1] ?? 0)],
                      ] as const
                    ).map(([k, c, v]) => (
                      <span key={k} style={sx('display:inline-flex;align-items:center;gap:5px;min-width:0;')}>
                        <i style={sx('display:inline-block;width:8px;height:2px;flex:0 0 8px;background:' + c + ';')} />
                        <span style={sx('font-size:14px;color:' + GREY + ';white-space:nowrap;')}>
                          <span>{k}</span>
                        </span>
                        <span style={sx('font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;color:' + INK + ';white-space:nowrap;')}>
                          <span>{v}</span>
                        </span>
                      </span>
                    ))}
                  </div>
                  <div style={sx(NOTE)}>
                    <span>
                      {Q.totalHours == null || Q.hoursPerMonth == null
                        ? 'Hours returned are not measured yet, so the quarter shows captured dollars per week from your own claims.'
                        : Q.label.split(' ')[0] +
                        ' total is measured: ' +
                        grp(Q.totalHours) +
                        ' hrs at ' +
                        grp(Q.hoursPerMonth) +
                        ' hrs / mo across the ' +
                        Q.windowLabel +
                        ' window. The week-by-week climb is a modeled adoption curve fitted to that total: no per-week feed exists. ' +
                        Q.label.split(' ')[0] +
                        ' is the first quarter with Aithora here, so there is no prior quarter to compare.'}
                    </span>
                  </div>
                  </>)}
                </div>
              )}
            </div>

            <div style={sx('display:contents;')}>
              <div style={sx(REG_WRAP)} data-testid="eff-ppd">
                {head('ppd', <>
                  <span style={sx(REG_HEAD)}>Cost per resident day</span>
                  <BuildTagged tag={b.ppd.basis}><span style={sx(RUN_TAG)}>
                    <span>{b.ppd.basis}</span>
                  </span></BuildTagged>
                  <span style={sx('flex:1 1 4px;')} />
                  <span style={sx(SUM(INK))}>
                    <span>{costPPD != null ? usd(costPPD) + ' to deliver a day' : 'no margin on record'}</span>
                  </span>
                </>, 6)}
                {isOpen('ppd') && (<>
                {costPPD != null &&
                  payers.map((p, i) => {
                    const m = p.ratePerDay - costPPD;
                    return (
                      <div key={p.name} style={sx('min-width:0;padding:4px 0;border-bottom:1px solid rgba(34,39,46,.06);animation:riseIn .26s cubic-bezier(.22,.61,.36,1);animation-delay:' + i * 40 + 'ms;')}>
                        <div style={sx('display:flex;align-items:baseline;gap:6px;min-width:0;padding-bottom:3px;')}>
                          <span style={sx('font-size:14px;font-weight:700;color:' + INK + ';white-space:nowrap;')}>
                            <span>{p.name}</span>
                          </span>
                          <span style={sx('font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;color:' + GREY + ';white-space:nowrap;')}>
                            <span>{usd(p.ratePerDay)}</span>
                          </span>
                          <span style={sx('margin-left:auto;font-size:14px;font-weight:800;font-variant-numeric:tabular-nums;white-space:nowrap;color:' + (m >= 0 ? GREEN : RED) + ';')}>
                            <span>{usd(m) + ' / day'}</span>
                          </span>
                        </div>
                        <i style={sx('display:block;height:9px;border-radius:2px;background:rgba(34,39,46,.13);width:' + Math.round((p.ratePerDay / maxPPD) * 100) + '%;animation:growW .5s cubic-bezier(.22,.61,.36,1);transform-origin:left;animation-delay:' + i * 40 + 'ms;')} />
                        <span style={sx('display:flex;height:9px;border-radius:2px;overflow:hidden;margin-top:2px;width:' + Math.round((costPPD / maxPPD) * 100) + '%;animation:growW .6s cubic-bezier(.22,.61,.36,1);transform-origin:left;animation-delay:' + (i * 40 + 60) + 'ms;')}>
                          {b.ppd.shares.map((s, si) => (
                            <i key={s.centre} style={sx('display:block;height:9px;flex:' + s.share + ' 1 0;background:' + SHARE_C[si % SHARE_C.length] + ';')} />
                          ))}
                        </span>
                      </div>
                    );
                  })}
                <div style={sx('display:flex;flex-wrap:wrap;gap:3px 10px;margin-top:5px;min-width:0;')}>
                  {costPPD != null &&
                    b.ppd.shares.map((s, si) => (
                      <span key={s.centre} style={sx('display:inline-flex;align-items:center;gap:4px;min-width:0;font-size:14px;color:' + GREY + ';white-space:nowrap;')}>
                        <i style={sx('display:inline-block;width:7px;height:7px;border-radius:2px;flex:0 0 7px;background:' + SHARE_C[si % SHARE_C.length] + ';')} />
                        <span>{s.centre + ' ' + usd(costPPD * s.share)}</span>
                      </span>
                    ))}
                </div>
                <div style={sx('font-size:14px;color:#54606C;line-height:1.3;margin-top:5px;text-wrap:pretty;')}>
                  <span>
                    {costPPD != null
                      ? 'Grey is the payer’s daily rate; the stack beneath is delivery cost, identical across payers. Medicaid at ' +
                        Math.round((medicaid?.share ?? 0) * 100) +
                        '% of census runs ' +
                        usd((medicaid?.ratePerDay ?? 0) - costPPD) +
                        ' a day underwater: that is the census argument. Cost is pinned to our measured margin; centre split from national cost reports.'
                      : 'Cost per day is pinned to the operating margin, and this building has none on record.'}
                  </span>
                </div>
                </>)}
              </div>
            </div>

            {b.stacks.map((names, si) => (
              <div key={si} style={sx('display:contents;')}>
                {names
                  .map((nm) => b.centres.find((c) => c.name === nm))
                  .filter((c): c is { name: string; keys: string[] } => !!c)
                  .map((c, ci) => {
                    const rows = c.keys.map((k) => byKey.get(k)).filter((r): r is EfficiencyLine => !!r);
                    const live = rows.filter((r) => r.basis !== 'LOCKED').length;
                    const cs = rows.reduce((t, r) => t + r.savedUsd, 0);
                    const cr = rows.reduce((t, r) => t + r.atRiskUsd, 0);
                    return (
                      <div key={c.name} data-testid="eff-panel" style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:6px 11px 7px;min-width:0;break-inside:avoid;margin-bottom:6px;animation:riseIn .26s cubic-bezier(.22,.61,.36,1);animation-delay:' + Math.min(200, ci * 26) + 'ms;')}>
                        {head('p-' + c.name, <>
                          <span style={sx(REG_HEAD)}>
                            <span>{c.name}</span>
                          </span>
                          <span style={sx('font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;color:' + GREY + ';white-space:nowrap;')}>
                            <span>{live + ' of ' + rows.length}</span>
                          </span>
                          <span style={sx('flex:1 1 4px;')} />
                          <span style={sx(SUM(cs >= cr ? GREEN : RED))}>
                            <span>{((cs > 0 ? '+' + k$(cs) : '') + (cs > 0 && cr > 0 ? '  ' : '') + (cr > 0 ? '−' + k$(cr) : '')) || (live ? '$0 change this month' : 'not measured yet')}</span>
                          </span>
                        </>, 7)}
                        {isOpen('p-' + c.name) && (<>
                        {rows.map((r) => {
                          const locked = r.basis === 'LOCKED';
                          const mag = Math.max(r.savedUsd, r.atRiskUsd);
                          const tone = r.savedUsd > 0 ? GREEN : r.atRiskUsd > 0 ? RED : 'rgba(34,39,46,.20)';
                          return (
                            <a
                              key={r.key}
                              href="#"
                              aria-expanded={openKey === r.key}
                              onClick={open(r.key)}
                              style={sx('display:grid;grid-template-columns:8px minmax(0,1fr) max-content;gap:7px;align-items:center;padding:3px 0;text-decoration:none;min-width:0;' + (locked ? 'opacity:.55;' : '') + sel(r.key))}
                            >
                              <span style={sx(dot(r.basis))} />
                              <span style={sx('min-width:0;')}>
                                <span style={sx('min-width:0;font-size:14px;color:' + INK + ';line-height:1.25;')}>
                                  <span>{r.line}</span>
                                </span>
                                <span style={sx('display:block;height:7px;border-radius:2px;background:rgba(34,39,46,.07);overflow:hidden;margin-top:2px;')}>
                                  <i style={sx(bStyle(mag, tone))} />
                                </span>
                              </span>
                              <span style={sx('text-align:right;font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;white-space:nowrap;color:' + (r.savedUsd > 0 ? GREEN : r.atRiskUsd > 0 ? RED : GREY) + ';')}>
                                <span>{lineValue(r)}</span>
                              </span>
                            </a>
                          );
                        })}
                        </>)}
                      </div>
                    );
                  })}
              </div>
            ))}

            <div style={sx('display:contents;')}>
              <div style={sx(REG_WRAP)} data-testid="eff-flow">
                {head('flow', <>
                  <span style={sx(REG_HEAD)}>Where the hours went</span>
                  <BuildTagged tag={b.flow.basis}><span style={sx(RUN_TAG)}>
                    <span>{b.flow.basis}</span>
                  </span></BuildTagged>
                  <span style={sx('flex:1 1 4px;')} />
                  <span style={sx(SUM(GREEN))}>
                    <span>{dashOr(hrsTotal, grp) + ' hrs / mo'}</span>
                  </span>
                </>, 6)}
                {isOpen('flow') && (<>
                {b.flow.rows.map((f, i) => (
                  <div key={f.k} style={sx('display:grid;grid-template-columns:minmax(0,1fr) max-content max-content;gap:6px;align-items:center;padding:3px 0;min-width:0;animation:riseIn .26s cubic-bezier(.22,.61,.36,1);animation-delay:' + i * 40 + 'ms;')}>
                    <span style={sx('min-width:0;font-size:14px;color:' + INK + ';line-height:1.2;white-space:normal;overflow-wrap:anywhere;')}>
                      <span>{f.k}</span>
                    </span>
                    <span style={sx('text-align:right;font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;color:' + INK + ';white-space:nowrap;')}>
                      <span>{dashOr(hrsTotal, (h) => grp(h * f.share)) + ' hrs'}</span>
                    </span>
                    <span style={sx('text-align:right;font-size:14px;font-variant-numeric:tabular-nums;color:' + GREY + ';white-space:nowrap;')}>
                      <span>{hrsTotal == null || b.loadedRnRate == null ? '–' : k$(hrsTotal * f.share * b.loadedRnRate)}</span>
                    </span>
                    <span style={sx('grid-column:1/-1;display:block;height:6px;border-radius:2px;background:rgba(34,39,46,.07);overflow:hidden;')}>
                      <i style={sx('display:block;height:6px;border-radius:2px;background:' + FLOW_C[i % FLOW_C.length] + ';width:' + Math.round(f.share * 100) + '%;animation:growW .5s cubic-bezier(.22,.61,.36,1);transform-origin:left;animation-delay:' + i * 40 + 'ms;')} />
                    </span>
                  </div>
                ))}
                <div style={sx('font-size:14px;color:#54606C;line-height:1.3;margin-top:5px;text-wrap:pretty;')}>
                  <span>{b.flow.note}</span>
                </div>
                </>)}
              </div>

              <div style={sx(REG_WRAP)} data-testid="eff-fill">
                {head('fill', <>
                  <span style={sx(REG_HEAD)}>Filling one open shift</span>
                  <span style={sx('flex:1 1 4px;')} />
                  <span style={sx('font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;white-space:nowrap;color:' + GREY + ';')}>
                    <span>{dashOr(b.fill.hours, String) + ' hrs · ' + b.fill.role}</span>
                  </span>
                </>, 6)}
                {isOpen('fill') && (<>
                {(() => {
                  const top = Math.max(1, ...b.fill.rows.map((f) => f.ratePerHour));
                  return b.fill.rows.map((f, i) => (
                    <div key={f.k} style={sx('display:grid;grid-template-columns:minmax(0,1fr) max-content max-content;gap:6px;align-items:center;padding:3px 0;min-width:0;animation:riseIn .26s cubic-bezier(.22,.61,.36,1);animation-delay:' + i * 40 + 'ms;')}>
                      <span style={sx('min-width:0;font-size:14px;font-weight:600;color:' + INK + ';')}>
                        <span>{f.k}</span>
                      </span>
                      <span style={sx('text-align:right;font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;color:' + FILL_C[i % FILL_C.length] + ';white-space:nowrap;')}>
                        <span>{'$' + f.ratePerHour.toFixed(2) + ' / hr'}</span>
                      </span>
                      <span style={sx('text-align:right;font-size:14px;font-variant-numeric:tabular-nums;color:' + GREY + ';white-space:nowrap;')}>
                        <span>{dashOr(b.fill.hours, (h) => '$' + Math.round(f.ratePerHour * h)) + ' / shift'}</span>
                      </span>
                      <span style={sx('grid-column:1/-1;display:block;height:6px;border-radius:2px;background:rgba(34,39,46,.07);overflow:hidden;')}>
                        <i style={sx('display:block;height:6px;border-radius:2px;background:' + FILL_C[i % FILL_C.length] + ';width:' + Math.round((f.ratePerHour / top) * 100) + '%;animation:growW .5s cubic-bezier(.22,.61,.36,1);transform-origin:left;animation-delay:' + i * 40 + 'ms;')} />
                      </span>
                    </div>
                  ));
                })()}
                <div style={sx('font-size:14px;color:#54606C;line-height:1.3;margin-top:5px;text-wrap:pretty;')}>
                  <span>{b.fill.note}</span>
                </div>
                </>)}
              </div>
            </div>

            <div style={sx('display:contents;')}>
              <div style={sx(REG_WRAP)} data-testid="eff-waterfall">
                {head('svr', <>
                  <span style={sx(REG_HEAD)}>Saved vs at risk</span>
                  <span style={sx('flex:1 1 4px;')} />
                  <span style={sx(SUM(GREEN))}>
                    <span>{'+' + k$(savedTotal) + ' saved · −' + k$(riskTotal) + ' at risk'}</span>
                  </span>
                </>, 7)}
                {isOpen('svr') && (<>
                {waterfall.map(([k, v, c], i) => (
                  <div key={k} style={sx('display:grid;grid-template-columns:minmax(0,1.6fr) minmax(40px,.9fr) max-content;gap:7px;align-items:center;padding:2px 0;min-width:0;animation:riseIn .24s cubic-bezier(.22,.61,.36,1);animation-delay:' + i * 18 + 'ms;')}>
                    <span style={sx('min-width:0;font-size:14px;color:' + INK + ';line-height:1.2;')}>
                      <span>{k}</span>
                    </span>
                    <span style={sx('display:block;height:8px;border-radius:2px;background:rgba(34,39,46,.07);overflow:hidden;')}>
                      <i style={sx(bStyle(Math.abs(v), c))} />
                    </span>
                    <span style={sx('text-align:right;font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;color:' + c + ';')}>
                      <span>{(v > 0 ? '+' : '−') + k$(Math.abs(v))}</span>
                    </span>
                  </div>
                ))}
                <div style={sx('font-size:14px;color:#54606C;line-height:1.3;margin-top:4px;')}>
                  <span>{'Saved ' + k$(savedTotal) + '  ·  at risk ' + k$(riskTotal) + '  ·  never netted'}</span>
                </div>
                </>)}
              </div>

              <div style={sx(REG_WRAP)} data-testid="eff-scenario">
                {head('scen', <>
                  <span style={sx(REG_HEAD)}>
                    <span>{'If we close it · margin now ' + (margin != null ? margin.toFixed(1) + '%' : '–')}</span>
                  </span>
                </>, 7)}
                {isOpen('scen') && (<>
                {b.scenarios.map((x, i) => {
                  const pts = revMo ? (x.usd / revMo) * 100 : 0;
                  return (
                    <div key={x.k} style={sx('display:grid;grid-template-columns:minmax(0,1.5fr) minmax(30px,.6fr) max-content max-content;gap:6px;align-items:center;padding:2px 0;min-width:0;animation:riseIn .24s cubic-bezier(.22,.61,.36,1);animation-delay:' + i * 18 + 'ms;')}>
                      <span style={sx('min-width:0;font-size:14px;color:' + INK + ';line-height:1.2;')}>
                        <span>{x.k}</span>
                      </span>
                      <span style={sx('display:block;height:8px;border-radius:2px;background:rgba(34,39,46,.07);overflow:hidden;')}>
                        <i style={sx(bStyle(x.usd, GREEN))} />
                      </span>
                      <span style={sx('text-align:right;font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;color:' + GREEN + ';')}>
                        <span>{'+' + pts.toFixed(1) + ' pp'}</span>
                      </span>
                      <span style={sx('text-align:right;font-size:14px;font-weight:800;font-variant-numeric:tabular-nums;color:' + INK + ';')}>
                        <span>{margin != null ? (margin + pts).toFixed(1) + '%' : '–'}</span>
                      </span>
                    </div>
                  );
                })}
                <div style={sx('font-size:14px;color:#54606C;line-height:1.3;margin-top:4px;')}>
                  <span>{'Each row priced at this facility’s own ' + money(revMo) + ' / mo revenue. One point of margin is ' + money(revMo / 100) + '.'}</span>
                </div>
                </>)}
              </div>
            </div>
          </div>

          <div style={sx(REG_WRAP + 'flex:0 0 auto;margin-bottom:0;padding-top:1px;')} data-testid="eff-coverage">
            <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:4px;min-width:0;')}>
              <span style={sx(REG_HEAD)}>
                <span>{liveN + ' of ' + L.length + ' lines live'}</span>
              </span>
              <span style={sx('flex:1 1 120px;min-width:0;')}>
                <span style={sx('display:block;height:9px;border-radius:2px;background:rgba(34,39,46,.09);overflow:hidden;margin-top:5px;')}>
                  <i style={sx('display:block;height:9px;border-radius:2px;background:' + GREEN + ';width:' + Math.round((liveN / Math.max(1, L.length)) * 100) + '%;animation:growW .55s cubic-bezier(.22,.61,.36,1);transform-origin:left;')} />
                </span>
              </span>
              <span style={sx('display:flex;align-items:center;gap:11px;flex-wrap:wrap;row-gap:3px;min-width:0;')}>
                {(
                  [
                    ['OURS', 'our records'],
                    ['CMS', 'CMS published'],
                    ['MODEL', 'cited range'],
                    ['LOCKED', 'no local value'],
                  ] as const
                ).map(([k, label]) => (
                  <span key={k} style={sx('display:inline-flex;align-items:center;gap:5px;font-size:14px;color:' + GREY + ';font-weight:600;white-space:nowrap;')}>
                    <span style={sx(dot(k))} />
                    {label}
                  </span>
                ))}
              </span>
              <a
                href="#"
                aria-expanded={lockOpen && !tall}
                onClick={(e) => {
                  pre(e);
                  setLockPref(!lockOpen);
                }}
                style={sx('display:inline-flex;align-items:center;height:22px;padding:0 9px;border-radius:5px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;background:rgba(34,39,46,.05);color:' + SLATE + ';border:1px solid rgba(34,39,46,.14);')}
              >
                <span>{(lockOpen ? 'Hide the ' : 'Show the ') + lockedN}</span>
              </a>
            </div>
            <div
              title={lockedN + ' need a payroll, PBJ, eMAR, survey or GL feed. Each carries the peer index it will be measured against: a benchmark with no local value is never priced.'}
              style={sx('font-size:14px;color:#54606C;line-height:1.35;margin-top:3px;min-width:0;overflow-wrap:break-word;')}
            >
              <span>{lockedN + ' need a payroll, PBJ, eMAR, survey or GL feed. Each carries the peer index it will be measured against: a benchmark with no local value is never priced.'}</span>
            </div>
            {lockOpen && !tall && (
              <div data-testid="eff-locked" style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(300px,1fr));gap:0 18px;min-width:0;margin-top:5px;')}>
                {L.filter((r) => r.basis === 'LOCKED').map((r) => (
                  <div key={r.key} style={sx('display:flex;flex-wrap:wrap;gap:2px 8px;align-items:baseline;padding:3px 0;border-bottom:1px solid rgba(34,39,46,.06);min-width:0;')}>
                    <span style={sx('flex:1 1 120px;min-width:0;font-size:14px;font-weight:600;color:' + INK + ';overflow-wrap:break-word;')}>
                      <span>{r.line}</span>
                    </span>
                    <span style={sx('flex:0 0 auto;text-align:right;font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;color:' + INK + ';white-space:nowrap;')}>
                      <span>{r.before !== null ? (r.unit === '$' ? money(r.before) : r.before + (r.unit ? ' ' + r.unit : '')) : 'no local value'}</span>
                    </span>
                    <span style={sx('flex:1 1 100%;min-width:0;font-size:14px;color:' + GREY + ';line-height:1.25;text-wrap:pretty;')}>
                      <span>{r.action}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:5px 11px 6px;min-width:0;flex:0 0 auto;')} data-testid="eff-donow">
            <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;row-gap:3px;min-width:0;')}>
              <span style={sx(REG_HEAD)}>Do now · ranked by dollars at stake</span>
              <span style={sx('flex:1 1 4px;')} />
              <span style={sx('font-size:14px;font-weight:700;color:' + GREY + ';white-space:nowrap;')}>
                <span>{'top ' + Math.min(6, doNow.length) + ' of ' + doNow.length + ' priced lines · owner named'}</span>
              </span>
            </div>
            <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(max(310px,calc(33.4% - 18px)),1fr));gap:0 18px;min-width:0;margin-top:2px;')}>
              {doNow.slice(0, 6).map((r) => (
                <a
                  key={r.key}
                  href="#"
                  title={r.line + ' · ' + r.action + ' · ' + r.owner}
                  onClick={open(r.key)}
                  style={sx('display:grid;grid-template-columns:7px minmax(0,1.15fr) max-content minmax(0,1.9fr) auto;gap:7px;align-items:baseline;padding:2px 0;text-decoration:none;min-width:0;border-top:1px solid rgba(34,39,46,.07);' + sel(r.key))}
                >
                  <span style={sx(dot(r.basis))} />
                  <span style={sx('min-width:0;font-size:14px;font-weight:700;color:' + INK + ';line-height:1.25;overflow-wrap:break-word;')}>
                    <span>{r.line}</span>
                  </span>
                  <span style={sx('font-size:14px;font-weight:800;font-variant-numeric:tabular-nums;text-align:right;white-space:nowrap;color:' + (r.savedUsd > 0 ? GREEN : RED) + ';')}>
                    <span>{r.savedUsd > 0 ? '+' + k$(r.savedUsd) : '−' + k$(r.atRiskUsd)}</span>
                  </span>
                  <span style={sx('min-width:0;font-size:14px;color:' + INK + ';line-height:1.25;overflow-wrap:break-word;')}>
                    <span>{r.action}</span>
                  </span>
                  <span style={sx('font-size:14px;font-weight:600;color:' + GREY + ';white-space:nowrap;')}>
                    <span>{r.owner}</span>
                  </span>
                </a>
              ))}
            </div>
          </div>

          {openRow && (
            <div role="dialog" aria-label={openRow.line} style={sx('position:absolute;right:10px;bottom:10px;z-index:40;width:min(400px,calc(100% - 20px));box-sizing:border-box;background:#FCFAF4;border:1px solid rgba(34,39,46,.20);border-radius:11px;box-shadow:0 16px 40px rgba(34,39,46,.20);padding:11px 13px 12px;animation:riseIn .2s cubic-bezier(.22,.61,.36,1);')}>
              <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;row-gap:4px;min-width:0;')}>
                <span style={sx('font-size:16px;font-weight:800;color:#22272E;min-width:0;')}>
                  <span>{openRow.line}</span>
                </span>
                <span
                  style={sx(
                    'display:inline-flex;align-items:center;height:17px;padding:0 6px;border-radius:4px;font-size:14px;font-weight:700;letter-spacing:.04em;white-space:nowrap;' +
                      (openRow.basis === 'OURS' ? 'color:' + GREEN + ';background:rgba(47,138,111,.11);' : openRow.basis === 'CMS' ? 'color:#2F5C97;background:rgba(79,138,214,.12);' : 'color:' + SLATE + ';background:transparent;border:1px dashed rgba(34,39,46,.32);'),
                  )}
                >
                  <span>{openRow.basis}</span>
                </span>
                <span style={sx('flex:1 1 4px;')} />
                <a
                  href="#"
                  onClick={(e) => {
                    pre(e);
                    setOpenKey(null);
                  }}
                  style={sx('font-size:14px;font-weight:700;color:#54606C;text-decoration:none;white-space:nowrap;')}
                >
                  Close
                </a>
              </div>
              <div style={sx('font-size:22px;font-weight:800;letter-spacing:-.02em;font-variant-numeric:tabular-nums;color:' + (openRow.savedUsd > 0 ? GREEN : openRow.atRiskUsd > 0 ? RED : INK) + ';margin-top:4px;')}>
                <span>
                  {openRow.savedUsd > 0
                    ? '+' + money(openRow.savedUsd) + ' / mo'
                    : openRow.atRiskUsd > 0
                      ? '−' + money(openRow.atRiskUsd) + ' / mo'
                      : openRow.before !== null
                        ? openRow.unit === '$'
                          ? money(openRow.before)
                          : openRow.before + ' ' + openRow.unit
                        : '–'}
                </span>
              </div>
              <div style={sx('font-size:14px;color:#54606C;margin-top:2px;')}>
                <span>{openRow.before !== null && openRow.now !== null ? 'before ' + openRow.before + ' → now ' + Number(openRow.now).toFixed(1) + ' ' + openRow.unit : ''}</span>
              </div>
              <div style={sx('font-size:14px;color:#3C454F;line-height:1.45;margin-top:7px;padding-top:6px;border-top:1px solid rgba(34,39,46,.12);text-wrap:pretty;')}>
                <span>{openRow.source}</span>
              </div>
              <div style={sx('display:flex;align-items:baseline;gap:7px;flex-wrap:wrap;row-gap:3px;margin-top:7px;padding-top:6px;border-top:1px solid rgba(34,39,46,.12);min-width:0;')}>
                <span style={sx('font-size:14px;letter-spacing:.05em;text-transform:uppercase;color:#54606C;font-weight:700;white-space:nowrap;')}>Do now</span>
                <span style={sx('flex:1 1 150px;min-width:0;font-size:14px;font-weight:700;color:#22272E;line-height:1.35;text-wrap:pretty;')}>
                  <span>{openRow.action}</span>
                </span>
              </div>
              <div style={sx('display:flex;align-items:baseline;gap:7px;margin-top:3px;min-width:0;')}>
                <span style={sx('font-size:14px;letter-spacing:.05em;text-transform:uppercase;color:#54606C;font-weight:700;white-space:nowrap;')}>Owner</span>
                <span style={sx('font-size:14px;font-weight:600;color:#3C454F;')}>
                  <span>{openRow.owner}</span>
                </span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
    </>
  );
}
